/**
 * Rows-per-page options for list pages that support a card/table view toggle.
 * Table view uses [10,25,50,100]; card view uses [12,24,48,96] since card
 * grids read better in multiples of 3/4/6 columns. Keep this as the single
 * source of truth — do not re-declare these arrays inline per page.
 */
export type TableViewMode = "card" | "table";
export const TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const CARD_PAGE_SIZE_OPTIONS = [12, 24, 48, 96] as const;

export function getPageSizeOptions(viewMode: TableViewMode): readonly number[] {
  return viewMode === "card" ? CARD_PAGE_SIZE_OPTIONS : TABLE_PAGE_SIZE_OPTIONS;
}

/**
 * Returns a page size guaranteed to be a valid option for `viewMode`. If
 * `pageSize` isn't in that view's option list (e.g. it was set while in the
 * other view, or came from a stale default/localStorage/URL param), snaps to
 * the closest available option instead of leaving a <select> holding a value
 * that isn't one of its own options.
 */
export function resolvePageSizeForViewMode(
  pageSize: number,
  viewMode: TableViewMode,
): number {
  const options = getPageSizeOptions(viewMode);
  if (options.includes(pageSize)) return pageSize;
  if (!pageSize || pageSize <= 0 || !Number.isFinite(pageSize)) return options[0];
  return options.reduce((closest, opt) =>
    Math.abs(opt - pageSize) < Math.abs(closest - pageSize) ? opt : closest,
  options[0]);
}

export function formatVersion(version: number | string | null | undefined): string {
  if (version === null || version === undefined || version === "") return "-";
  const str = String(version);
  return str.startsWith("v") || str.startsWith("V") ? str : `v${str}`;
}

/**
 * Safely formats a date-like input. Returns `fallback` (default "-") for
 * null, undefined, empty strings, or unparseable dates — never "Invalid Date".
 * Locale defaults to "en-GB" (DD/MM/YYYY) to match existing card conventions.
 */
export function safeFormatDate(
  value: string | number | Date | null | undefined,
  locale: string = "en-GB",
  fallback: string = "-",
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return fallback;
  try {
    return d.toLocaleDateString(locale);
  } catch {
    return fallback;
  }
}
